import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Form, Input, Select, DatePicker, Button, message, Tag, Typography, Tabs, List, Empty, Popconfirm, Space } from 'antd';
import { PlusOutlined, CheckCircleFilled, ClockCircleOutlined, DeleteOutlined, CalendarOutlined, RetweetOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../utils/api';

const { Title, Text } = Typography;
const { Option } = Select;

export default function Tasks() {
  const [users, setUsers] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [recurring, setRecurring] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const recurrence = Form.useWatch('recurrence', form);
  const weekdays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  const fetchUsers = async () => {
    try { const res = await api.get('/auth/users'); setUsers(res.data || []); } catch (err) { console.error(err); }
  };
  const fetchTasks = async () => {
    setLoading(true);
    try { const res = await api.get('/tasks/all'); setTasks(res.data || []); } catch (err) { console.error(err); }
    setLoading(false);
  };
  const fetchRecurring = async () => {
    try { const res = await api.get('/tasks/recurring'); setRecurring(res.data || []); } catch (err) { console.error(err); }
  };

  useEffect(() => { fetchUsers(); fetchTasks(); fetchRecurring(); }, []);

  const assign = async (values) => {
    try {
      const rec = values.recurrence || 'none';
      await api.post('/tasks', {
        user_id: values.user_id || 'all',
        title: values.title,
        task_date: (values.task_date || dayjs()).format('YYYY-MM-DD'),
        priority: values.priority || 'normal',
        recurrence: rec,
        recur_dow: rec === 'weekly' ? (values.recur_dow ?? dayjs().day()) : undefined,
      });
      message.success(rec === 'none' ? 'Task assigned!' : 'Recurring task set!');
      form.resetFields();
      fetchTasks();
      fetchRecurring();
    } catch (err) { message.error(err.response?.data?.error || 'Failed'); }
  };

  const removeTask = async (id) => {
    try { await api.delete(`/tasks/${id}`); fetchTasks(); } catch (err) { message.error('Failed'); }
  };

  const removeRecurring = async (id) => {
    try { await api.delete(`/tasks/${id}`); message.success('Recurring task stopped'); fetchRecurring(); } catch (err) { message.error('Failed'); }
  };

  // Group tasks by date (desc) for the Daily Report
  const byDate = {};
  tasks.forEach(t => {
    const d = t.task_date || (t.created_at ? t.created_at.slice(0, 10) : 'No date');
    (byDate[d] = byDate[d] || []).push(t);
  });
  const dates = Object.keys(byDate).sort((a, b) => (a < b ? 1 : -1));

  const statusTag = (t) => t.status === 'done'
    ? <Tag color="green" icon={<CheckCircleFilled />}>Done{t.done_at ? ` · ${dayjs(t.done_at).format('DD MMM HH:mm')}` : ''}</Tag>
    : <Tag color="orange" icon={<ClockCircleOutlined />}>Pending</Tag>;

  const taskRow = (t) => (
    <List.Item
      key={t.id}
      actions={[
        <Popconfirm key="del" title="Delete this task?" onConfirm={() => removeTask(t.id)}>
          <Button size="small" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ]}
    >
      <List.Item.Meta
        title={<Space wrap>{t.title} {t.priority === 'urgent' && <Tag color="red">URGENT</Tag>}</Space>}
        description={<Space wrap><Text type="secondary">For: {t.assignee_name || 'Everyone'}</Text>{statusTag(t)}</Space>}
      />
    </List.Item>
  );

  return (
    <div>
      <Title level={3} style={{ margin: '0 0 16px' }}>Staff Tasks</Title>

      <Card style={{ marginBottom: 16, borderRadius: 10 }} title="Assign a task">
        <Form form={form} layout="vertical" onFinish={assign}>
          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item name="user_id" label="Assign to">
                <Select placeholder="Everyone" allowClear showSearch optionFilterProp="children">
                  {users.map(u => <Option key={u.id} value={u.id}>{u.full_name} ({u.role})</Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="task_date" label="Date" initialValue={dayjs()}>
                <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="priority" label="Priority" initialValue="normal">
                <Select>
                  <Option value="normal">Normal</Option>
                  <Option value="urgent">Urgent</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="recurrence" label="Repeat" initialValue="none">
                <Select>
                  <Option value="none">One time</Option>
                  <Option value="daily">Daily (roz)</Option>
                  <Option value="weekly">Weekly (har hafte)</Option>
                </Select>
              </Form.Item>
            </Col>
            {recurrence === 'weekly' && (
              <Col xs={24} sm={8}>
                <Form.Item name="recur_dow" label="Which day" initialValue={dayjs().day()}>
                  <Select>
                    {weekdays.map((d, i) => <Option key={i} value={i}>{d}</Option>)}
                  </Select>
                </Form.Item>
              </Col>
            )}
          </Row>
          <Form.Item name="title" label="Task / work" rules={[{ required: true, message: 'Task text required' }]}>
            <Input.TextArea rows={2} placeholder="e.g. Make 10 thumbnails today..." />
          </Form.Item>
          <Button type="primary" icon={<PlusOutlined />} htmlType="submit">Assign Task</Button>
        </Form>
      </Card>

      <Card style={{ borderRadius: 10 }}>
        <Tabs
          defaultActiveKey="report"
          items={[
            {
              key: 'report',
              label: <span><CalendarOutlined /> Daily Report</span>,
              children: dates.length === 0 ? <Empty description="No tasks yet" /> : (
                <div>
                  {dates.map(d => {
                    const list = byDate[d];
                    const done = list.filter(t => t.status === 'done').length;
                    return (
                      <Card key={d} type="inner" style={{ marginBottom: 12 }}
                        title={<Space><CalendarOutlined /><b>{d === 'No date' ? 'No date' : dayjs(d).format('DD MMM YYYY (ddd)')}</b></Space>}
                        extra={<Text type="secondary">{done}/{list.length} done</Text>}>
                        <List dataSource={list} loading={loading} renderItem={taskRow} />
                      </Card>
                    );
                  })}
                </div>
              )
            },
            {
              key: 'all',
              label: 'All Tasks',
              children: <List dataSource={tasks} loading={loading} renderItem={taskRow} locale={{ emptyText: 'No tasks' }} />
            },
            {
              key: 'recurring',
              label: <span><RetweetOutlined /> Recurring ({recurring.length})</span>,
              children: recurring.length === 0 ? <Empty description="No recurring tasks" /> : (
                <List
                  dataSource={recurring}
                  renderItem={(t) => (
                    <List.Item
                      key={t.id}
                      actions={[
                        <Popconfirm key="del" title="Stop this recurring task?" onConfirm={() => removeRecurring(t.id)}>
                          <Button size="small" danger icon={<DeleteOutlined />}>Stop</Button>
                        </Popconfirm>
                      ]}
                    >
                      <List.Item.Meta
                        title={<Space wrap>{t.title} {t.priority === 'urgent' && <Tag color="red">URGENT</Tag>}</Space>}
                        description={<Space wrap>
                          <Tag color="blue" icon={<RetweetOutlined />}>{t.recurrence === 'weekly' ? `Weekly - ${weekdays[t.recur_dow] || ''}` : 'Daily'}</Tag>
                          <Text type="secondary">For: {t.assignee_name || 'Everyone'}</Text>
                        </Space>}
                      />
                    </List.Item>
                  )}
                />
              )
            }
          ]}
        />
      </Card>
    </div>
  );
}
